import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { type z } from 'zod';

import { assertNever, isDefined, isTruthy } from '../utils/types.js';

export enum HistoryVariant {
  PushHistory = 'PushHistory',
  ReplaceHistory = 'ReplaceHistory',
}
interface UseQueryParamsProps<Schema extends z.ZodObject> {
  /**
   * Note: All query params are optional, and this schema will be treated as `partial()` internally.
   */
  schema: Schema;

  /**
   * Optional initial value to use when query params are not present. Default is `{}`
   * Note - URL will be updated with this initial value.
   */
  initialValues?: Partial<z.output<Schema>>;

  /**
   * Optional fallback value to use when query params are not present. Default is `{}`
   * Note - URL will not be updated with this fallback value.
   * This is used when the URL doesn't match the query schema.
   */
  fallbackValues?: Partial<z.output<Schema>>;

  /**
   * Optional variant to use when updating the URL. Default is `replaceHistory`
   * - `pushHistory` - Pushes a new entry onto the history stack.
   * - `replaceHistory` - Replaces the current entry on the history stack.
   */
  variant?: HistoryVariant;
}

interface QueryReturnValue<T> {
  queryParams: Partial<T>;
  search: string;
}

interface UseQueryParamsValue<T> {
  queryParams: Partial<T>;
  setQueryParams: (newQueryParams: Partial<T>) => QueryReturnValue<T>;
  updateQueryParams: (newQueryParams: Partial<T>) => QueryReturnValue<T>;
  updateQueryParam: <Field extends keyof T>(
    fieldName: Field,
    newQueryParam: T[Field] | null
  ) => QueryReturnValue<T>;
  removeQueryParam: (queryKey: keyof Partial<T>) => QueryReturnValue<T>;
  removeAllQueryParams: () => void;
}

/**
 * Provides a type-safe way to interact with query params.
 * Keep in mind that all query params are optional since anyone can edit the URL.
 * The goal is to parse and validate the query parameters to match your schema, without type casting.
 * This allows you to trust that the query parameters are shaped as expected, and falls back to an empty object if not.
 */
export function useQueryParams<Schema extends z.ZodObject>(
  props: UseQueryParamsProps<Schema>
): UseQueryParamsValue<z.output<Schema>> {
  type T = z.output<Schema>;
  const { schema, initialValues, fallbackValues, variant = HistoryVariant.ReplaceHistory } = props;
  const { search } = useLocation();
  const history = useHistory();
  const schemaRef = useRef(schema);
  const fallbackValueRef = useRef(fallbackValues ?? {});
  const initialValueRef = useRef<Partial<T> | undefined>(initialValues);
  const initialRedirectValueRef = useRef<Partial<T> | undefined>(undefined);

  const setQueryParams = useCallback(
    (newQueryParams: Partial<T>) => {
      const newParams = qs.stringify(newQueryParams, { addQueryPrefix: true });
      if (newParams === search) {
        return { queryParams: newQueryParams, search };
      }
      const historyUpdate = { search: newParams };
      switch (variant) {
        case HistoryVariant.PushHistory:
          history.push(historyUpdate);
          break;
        case HistoryVariant.ReplaceHistory:
          history.replace(historyUpdate);
          break;
        default:
          assertNever('Unknown variant', variant);
      }
      return { queryParams: newQueryParams, search: newParams };
    },
    [history, search, variant]
  );

  const updateQueryParams = useCallback(
    (newQueryParams: Partial<T>) => {
      const queryParams = qs.parse(search, { ignoreQueryPrefix: true });
      return setQueryParams({ ...queryParams, ...newQueryParams } as T);
    },
    [setQueryParams, search]
  );

  const updateQueryParam = useCallback(
    <Field extends keyof T>(fieldName: Field, newQueryParam: T[Field] | null) => {
      const queryParams = qs.parse(search, { ignoreQueryPrefix: true }) as T;
      if (isDefined(newQueryParam)) {
        queryParams[fieldName] = newQueryParam;
      } else {
        delete queryParams[fieldName];
      }
      return setQueryParams(queryParams);
    },
    [search, setQueryParams]
  );

  const removeQueryParam = useCallback(
    (queryKey: keyof Partial<T>) => {
      const queryParams = qs.parse(search, { ignoreQueryPrefix: true }) as T;
      delete queryParams[queryKey];
      return setQueryParams(queryParams);
    },
    [setQueryParams, search]
  );

  const removeAllQueryParams = useCallback(() => {
    switch (variant) {
      case HistoryVariant.PushHistory:
        history.push({ search: '' });
        break;
      case HistoryVariant.ReplaceHistory:
        history.replace({ search: '' });
        break;
      default:
        assertNever('Unknown variant', variant);
    }
  }, [history, variant]);

  useEffect(() => {
    if (isDefined(initialRedirectValueRef.current)) {
      if (!isTruthy(search)) {
        updateQueryParams(initialRedirectValueRef.current);
      }
      initialRedirectValueRef.current = undefined;
    }
  }, [updateQueryParams, search]);

  const queryParams = useMemo(() => {
    const parsedQueryParams = qs.parse(search, { ignoreQueryPrefix: true });
    // eslint-disable-next-line react-hooks/refs
    const parsedData = schemaRef.current.partial().safeParse(parsedQueryParams).data;
    /**
     * queryParams are often used in conjunction with API fetching. If the caller defines
     * initialValues, we want to ensure that the initial value is used only once.
     * We also want to transfer those values into the URL, so we have to default to it,
     * then move it for the `useEffect` to pick it up.
     */
    // eslint-disable-next-line react-hooks/refs
    const defaultValues = initialValueRef.current ?? fallbackValueRef.current;
    // eslint-disable-next-line react-hooks/refs
    initialRedirectValueRef.current = initialValueRef.current;
    // eslint-disable-next-line react-hooks/refs
    initialValueRef.current = undefined;
    return Object.keys(parsedData ?? {}).length ? parsedData! : defaultValues;
  }, [search]);

  // eslint-disable-next-line react-hooks/refs
  return {
    // eslint-disable-next-line react-hooks/refs
    queryParams,
    setQueryParams,
    updateQueryParams,
    updateQueryParam,
    removeQueryParam,
    removeAllQueryParams,
  };
}
